import React, { useState, useMemo } from 'react';
import { Typography, Box, Link, useTheme } from '@ui/index';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface SectionHelperTextProps {
  text: string;
  isHtml?: boolean | undefined;
  wordLimit?: number | undefined;
}

/**
 * Component to render section helper text with optional HTML support and read more/collapse functionality
 */
const SectionHelperText: React.FC<SectionHelperTextProps> = ({ 
  text, 
  isHtml = false, 
  wordLimit 
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  // Function to count words in text (strips HTML tags for accurate count)
  const countWords = (str: string): number => {
    const plainText = str.replace(/<[^>]*>/g, ' ');
    return plainText.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Function to truncate text to word limit
  const getTruncatedText = (str: string, limit: number): string => {
    if (!limit) return str;
    
    const words: string[] = [];
    let currentWord = '';
    let insideTag = false;
    let wordCount = 0;
    
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      
      if (char === '<') {
        insideTag = true;
        currentWord += char;
      } else if (char === '>') {
        insideTag = false;
        currentWord += char;
      } else if (!insideTag && /\s/.test(char)) {
        if (currentWord.replace(/<[^>]*>/g, '').trim().length > 0) {
          wordCount++;
          words.push(currentWord + char);
          if (wordCount >= limit) {
            break;
          }
          currentWord = '';
        } else if (currentWord.length > 0) {
          words.push(currentWord + char);
          currentWord = '';
        }
      } else {
        currentWord += char;
      }
    }
    
    // Add the last word if we haven't reached the limit
    if (wordCount < limit && currentWord.replace(/<[^>]*>/g, '').trim().length > 0) {
      words.push(currentWord);
    }
    
    return words.join('') + '...';
  };

  const { shouldTruncate, displayText } = useMemo(() => {
    if (!wordLimit) {
      return { shouldTruncate: false, displayText: text };
    }

    const wordCount = countWords(text);
    const shouldTrunc = wordCount > wordLimit;

    if (shouldTrunc && !isExpanded) {
      return { 
        shouldTruncate: true, 
        displayText: getTruncatedText(text, wordLimit) 
      };
    }

    return { shouldTruncate: shouldTrunc, displayText: text };
  }, [text, wordLimit, isExpanded]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box data-testid="components-section-helper-text-container-action" sx={{ width: '100%' }}>
      {isHtml ? (
        <Typography data-testid="components-section-helper-text-text-action-1" 
          variant="body2" 
          sx={{ 
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            mb: shouldTruncate ? 1 : 0,
            '& strong': {
              fontWeight: 700,
              color: theme.palette.text.secondary,
            },
            '& p': {
              margin: 0,
              marginBottom: 1,
              '&:last-child': {
                marginBottom: 0,
              },
            },
          }}
          dangerouslySetInnerHTML={{ __html: displayText }}
        />
      ) : (
        <Typography data-testid="components-section-helper-text-text-action-2" 
          variant="body2" 
          sx={{ 
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            mb: shouldTruncate ? 1 : 0,
          }}
        >
          {displayText}
        </Typography>
      )}
      
      {shouldTruncate && (
        <Box data-testid="components-section-helper-text-container-handle-toggle" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
          }}
          onClick={handleToggle}
        >
          <Link data-testid="components-section-helper-text-link-action"
            component="span"
            sx={{
              color: (theme.palette as any).brand?.main || '#155F54',
              fontSize: '16px',
              textDecorationLine: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: (theme.palette as any).brand?.dark || '#0d3f38',
              },
            }}
          >
            {isExpanded ? 'Collapse' : 'Read more'}
          </Link>
          {isExpanded ? (
            <KeyboardArrowUpIcon 
              sx={{ 
                fontSize: 16,
                color: (theme.palette as any).brand?.main || '#155F54',
              }} 
            />
          ) : (
            <KeyboardArrowDownIcon 
              sx={{ 
                fontSize: 16,
                color: (theme.palette as any).brand?.main || '#155F54',
              }} 
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default SectionHelperText;

